import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  Link,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { SelecFilterChip } from 'src/App/Components/Chips';
import {
  BACKGROUND_COLOR_1,
  BACKGROUND_COLOR_2,
  COLOR_ERROR,
  COLOR_ERROR_BACKGROUNG,
  COLOR_GREY_200,
  COLOR_SUCCESS_1,
  COLOR_SUCCESS_BACKGROUNG,
  COLOR_TERTIARY_1_500,
  COLOR_TEXT_2,
  SIDEBAR_COLOR,
} from 'src/colors';
import { callApi } from 'src/core/callApi';

const User = ({ adminPassword, dataType, showSearchbar, setShowSearchbar }) => {
  const [userEmailId, setUserEmailId] = useState<any>('');
  const [userDetails, setUserDetails] = useState<any>();
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [subscribedInsightRegionRows, setSubscribedInsightRegionRows] = useState<any>();
  const [searchedInsightsRegionRows, setSearchedInsightRegionRows] = useState<any>();
  const [selectedDashboard, setSelectedDashboard] = useState<string>('insights');
  const [estimatorReportRows, setEstimatorReportRows] = useState<any>();
  const [openAddCreditsModal, setOpenAddCreditsModal] = useState<boolean>(false);
  const [showUserNotFoundAlert, setShowUserNotFoundAlert] = useState<boolean>(false);
  const [showNotAdminUserAlert, setShowNotAdminUserAlert] = useState<boolean>(false);
  const [openDeleteUserModal, setOpenDeleteUserModal] = useState<boolean>(false);

  const subscribedInsightsRegionColumn: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 50 },
    {
      field: 'region',
      headerName: 'Region',
      width: 200,
      editable: false,
    },
    {
      field: 'city',
      headerName: 'City',
      width: 120,
      editable: false,
    },
    {
      field: 'country',
      headerName: 'Country',
      width: 200,
      editable: false,
    },
    {
      field: 'started_at_dates',
      headerName: 'Start Date',
      width: 150,
      editable: false,
    },
    {
      field: 'ended_at_dates',
      headerName: 'End Date',
      width: 150,
      editable: false,
      renderCell: params => (
        <div style={{ color: new Date(params.value) < new Date() ? 'red' : '' }}>
          {params.value}
        </div>
      ),
    },
  ];

  const estimatorReportColumn: GridColDef[] = [
    { field: 'index', headerName: 'Index', width: 50 },
    { field: 'id', headerName: 'ID', width: 300 },
    {
      field: 'address_full',
      headerName: 'Address Full',
      width: 200,
      editable: false,
    },
    {
      field: 'address_short',
      headerName: 'Address Short',
      width: 200,
      editable: false,
    },
    {
      field: 'bedrooms',
      headerName: 'Bedrooms',
      width: 100,
      editable: false,
    },
    {
      field: 'report_type',
      headerName: 'Report Type',
      width: 100,
      editable: false,
    },
    {
      field: 'coordinates',
      headerName: 'Coordinates',
      width: 200,
      editable: false,
    },
    {
      field: 'user_report_access_type',
      headerName: 'User Report Access Type',
      width: 100,
      editable: false,
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 150,
      editable: false,
    },
    {
      field: 'report_url',
      headerName: 'Report Url',
      width: 600,
      renderCell: (params) => (
        <Link href={params.value} target="_blank">
          {params.value}
        </Link>
      )
    } 
  ]

  const searchedRegionColumn: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    {
      field: 'district',
      headerName: 'District Name',
      width: 260,
      editable: false,
    },
  ];

  useEffect(() => {
    if (userDetails) {
      setShowSearchbar(false);
      // set insights region in subscribedInsightRegionRows
      let rows: any = [];
      userDetails.regions.map((data, index) => {
        let tempRows = {
          id: index + 1,
          region: data?.region,
          city: data?.city,
          country: data?.country,
          started_at_dates: data?.started_at_dates?.slice(-1)[0],
          ended_at_dates: data?.ended_at_dates?.slice(-1)[0],
        };
        rows.push(tempRows);
      });
      setSubscribedInsightRegionRows(rows);

      // set Insights searched region in setSearchedInsightRegionRows
      let InsightSearchRows: any = [];
      userDetails.searchedRegions.map((region, index) => {
        let tempRows = {
          id: index + 1,
          district: region,
        };
        InsightSearchRows.push(tempRows);
      });
      setSearchedInsightRegionRows(InsightSearchRows);
      // console.log(InsightSearchRows);

      // Set estimator reports
      let tmpEstimatorReportRows: any = [];
      userDetails['user_estimator_reports'].map((data, index) => {
        let tempRows = {
          index: index + 1,
          id: data?.id,
          address_full: data?.address_full,
          address_short: data?.address_short,
          bedrooms: data?.bedrooms,
          report_type: data?.report_type,
          coordinates: (`${data?.latitude}, ${data?.longitude}`),
          user_report_access_type: data?.user_report_access_type,
          created_at: data?.created_at,
          report_url: window.location.origin + '/' + data?.url_path
        };
        tmpEstimatorReportRows.push(tempRows);
      });
      setEstimatorReportRows(tmpEstimatorReportRows);      
    }
  }, [userDetails]);

  const fetchUserdata = async () => {
    setShowLoader(true);
    setShowUserNotFoundAlert(false);
    setShowNotAdminUserAlert(false);
    const url =
      'https://b9af2vkl1m.execute-api.us-east-2.amazonaws.com/default/getAirbticsUserDetails';
    const config = {
      queryParams: {
        admin_password: encodeURIComponent(adminPassword),
        search_user_email: encodeURIComponent(userEmailId),
      },
    };
    const res = await callApi(url, config, false);
    if (res.message == 'User not found in the database') {
      setShowLoader(false);
      setShowUserNotFoundAlert(true);
    } else if (res.message == 'Not a Admin User') {
      setShowLoader(false);
      setShowNotAdminUserAlert(true);
    } else {
      setUserDetails(res.message);
      setShowLoader(false);
    }
  };

  const convertTimestamp = (timestamp: number, timezone: string) => {
    const date = new Date(timestamp * 1000);

    const options: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false,
    };

    const time = date.toLocaleString('en-US', options);

    // options.timeZone = 'UTC';
    // const utcTime = date.toLocaleString('en-US', options);

    return time;
  };

  const AddCreditModal = () => {
    const [emailID, setEmailID] = useState<string>(userEmailId);
    const [addCreditsResponse, setAddCreditsResponse] = useState<string>();
    const [showLoaderInAddCreditModal, setShowLoaderInAddCreditModal] = useState<boolean>(false);
    const [selectedPlan, setSelectedPlan] = useState<string>('pro');
    const [duration, setDuration] = useState<string>('month');

    const handleAddCreditBtn = async () => {
      setShowLoaderInAddCreditModal(true);
      setAddCreditsResponse('');
      const config = {
        queryParams: {
          email: encodeURIComponent(emailID),
          duration: duration,
          planName: selectedPlan,
        },
      };

      const res: any = await callApi(
        'https://7pbdt7zelg.execute-api.us-east-2.amazonaws.com/default/addUserCreditsToDynamoDB',
        config,
        true
      );
      setAddCreditsResponse(res.message);
      setShowLoaderInAddCreditModal(false);
      if (res.message.includes('Successfully')) {
        fetchUserdata();
        setOpenAddCreditsModal(false);
      }
    };

    return (
      <Modal
        open={openAddCreditsModal}
        onClose={() => {
          setOpenAddCreditsModal(!openAddCreditsModal);
        }}
      >
        <Box
          sx={{
            width: '50vw',
            height: '50vh',
            background: '#fff',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            padding: '20px',
            borderRadius: 4,
          }}
        >
          <Typography variant="h5" style={{ textAlign: 'center' }}>
            Add Credits To User DynamoDB
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              marginTop: '10px',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography style={{ width: '200px', display: 'flex', alignItems: 'center' }}>
                Email ID:
              </Typography>
              <TextField
              sx={{width:'200px'}}
                id="outlined-basic"
                variant="outlined"
                value={emailID}
                onChange={e => {
                  setEmailID(e.target.value);
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography style={{ width: '200px', display: 'flex', alignItems: 'center' }}>
                Plan:
              </Typography>
              <FormControl>
                <Select
                sx={{width:'200px'}}
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={selectedPlan}
                  label=""
                  onChange={e => {
                    setSelectedPlan(e.target.value);
                  }}
                >
                  <MenuItem value={'pro'}>Pro</MenuItem>
                  <MenuItem value={'expert'}>Expert</MenuItem>
                  <MenuItem value={'scale'}>Scale</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography style={{ width: '200px', display: 'flex', alignItems: 'center' }}>
                Duration:
              </Typography>
              <FormControl>
                <Select
                sx={{width:'200px'}}
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={duration}
                  label=""
                  onChange={e => {
                    setDuration(e.target.value);
                  }}
                >
                  <MenuItem value={'month'}>Month</MenuItem>
                  <MenuItem value={'year'}>Year</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Button
              style={{ width: '80%', backgroundColor: '#52C879', color: 'white' }}
              onClick={handleAddCreditBtn}
            >
              {showLoaderInAddCreditModal ? <CircularProgress size={20} sx={{color:SIDEBAR_COLOR}}/> : 'Give Access'}
            </Button>
            {addCreditsResponse && addCreditsResponse.includes('Successfully') && (
              <Alert severity="success" sx={{ width: '75%' }}>
                {addCreditsResponse}
              </Alert>
            )}

            {addCreditsResponse && !addCreditsResponse.includes('Successfully added') && (
              <Alert severity="error" sx={{ width: '75%' }}>
                {addCreditsResponse}
              </Alert>
            )}
          </Box>
        </Box>
      </Modal>
    );
  };

  const DeleteUserModal = () => {
    const [deletedBy, setDeletedBy] = useState<string>('');
    const [response, setResponse] = useState<string>();
    const [showLoaderInDeleteUserModal, setShowLoaderInDeleteUserModal] = useState<boolean>(false);

    const handleDeleteUserBtn = async () => {
      setShowLoaderInDeleteUserModal(true);
      setResponse('');
      const config = {
        queryParams: {
          deletedUser: encodeURIComponent(userEmailId),
          deletedBy: deletedBy,
        },
      };
      if (deletedBy) {
        const res: any = await callApi(
          'https://n4k7t590h1.execute-api.us-east-2.amazonaws.com/default/deleteUserFromDynamoDB',
          config,
          true
        );
        setResponse(res.message);
        setShowLoaderInDeleteUserModal(false);
      } else {
        setResponse('Enter you Name');
        setShowLoaderInDeleteUserModal(false);
      }
    };

    return (
      <Modal
        open={openDeleteUserModal}
        onClose={() => {
          setOpenDeleteUserModal(!openDeleteUserModal);
        }}
      >
        <Box
          sx={{
            width: '35vw',
            height: '35vh',
            background: '#fff',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            padding: '30px',
            borderRadius: 4,
          }}
        >
          <Typography variant="h5" style={{ textAlign: 'center', width: '100%' }}>
            Do you want to delete user{' '}
            <Typography variant="h5" sx={{ color: COLOR_TERTIARY_1_500 }}>
              {userEmailId}?
            </Typography>
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              marginTop: '10px',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography style={{ width: '200px', display: 'flex', alignItems: 'center' }}>
                Your Name:
              </Typography>
              <TextField
                id="outlined-basic"
                variant="outlined"
                value={deletedBy}
                onChange={e => {
                  setDeletedBy(e.target.value);
                }}
              />
            </Box>
            <Button
              style={{ width: '80%', backgroundColor: '#52C879', color: 'white' }}
              onClick={handleDeleteUserBtn}
            >
              Delete User
            </Button>
            {showLoaderInDeleteUserModal && <CircularProgress />}
            {response && response.includes('User Deleted Successfully') && (
              <Alert severity="success" sx={{ width: '75%' }}>
                {response}
              </Alert>
            )}

            {response && !response.includes('User Deleted Successfully') && (
              <Alert severity="error" sx={{ width: '75%' }}>
                {response}
              </Alert>
            )}
          </Box>
        </Box>
      </Modal>
    );
  };
  return (
    <Box sx={{ margin: '10px' }}>
      <AddCreditModal />
      <DeleteUserModal />
      <Grid spacing={2} container sx={{ width: '100%' }} alignItems={'flex-start'}>
        <Grid item xs={12}>
          {dataType == 'user' && (
            <Box>
              {showSearchbar && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    id="outlined-basic"
                    label="email"
                    variant="outlined"
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: '10px 0px 0px 10px',
                      width: '50%',
                      '&:focus': {
                        border: 'none',
                        outline: 'none',
                      },
                    }}
                    onChange={e => setUserEmailId(e.target.value)}
                  />
                  <Button
                    sx={{
                      backgroundColor: COLOR_SUCCESS_1,
                      color: 'white',
                      height: '56px',
                      width: '85px',
                      borderRadius: '0px 10px 10px 0px',
                    }}
                    onClick={fetchUserdata}
                  >
                    Search
                  </Button>
                </Box>
              )}
              {showLoader && (
                <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: '10px' }}>
                  <CircularProgress />
                </Box>
              )}

              {showUserNotFoundAlert && (
                <Alert severity="error" sx={{ marginTop: '20px' }}>
                  User not found in the database
                </Alert>
              )}

              {showNotAdminUserAlert && (
                <Alert severity="error" sx={{ marginTop: '20px' }}>
                  Your Admin Password Is Incorrect
                </Alert>
              )}

              {!showSearchbar && userDetails && (
                <Box sx={{ backgroundColor: '#B2BEB5', height: '620px' }}>
                  <Grid container>
                    <Grid item sm={12} md={12} lg={12} sx={{ height: '70px' }}>
                      <Box
                        sx={{ display: 'flex', justifyContent: 'space-between', margin: '10px' }}
                      >
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {userDetails?.userName}
                          </Typography>
                          <Typography variant="body1">{userDetails?.email}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                          {userDetails?.isDeleted && (
                            <SelecFilterChip
                              height="30px"
                              width="120px"
                              borderRadius="30px"
                              colors={{
                                backgroundColor: COLOR_ERROR_BACKGROUNG,
                                color: COLOR_ERROR,
                                borderColor: COLOR_ERROR_BACKGROUNG,
                              }}
                              text="Deleted"
                              fontWeight="bold"
                              handleOnClick={() => {}}
                              isSelected={false}
                              tooltip=""
                            />
                          )}
                          {userDetails?.isVerified && (
                            <SelecFilterChip
                              height="30px"
                              width="120px"
                              borderRadius="30px"
                              colors={{
                                backgroundColor: COLOR_SUCCESS_BACKGROUNG,
                                color: COLOR_SUCCESS_1,
                                borderColor: COLOR_SUCCESS_BACKGROUNG,
                              }}
                              text="Verified"
                              fontWeight="bold"
                              handleOnClick={() => {}}
                              isSelected={false}
                              tooltip=""
                            />
                          )}
                          {userDetails?.isSubscribed && (
                            <SelecFilterChip
                              height="30px"
                              width="120px"
                              borderRadius="30px"
                              colors={{
                                backgroundColor: COLOR_SUCCESS_BACKGROUNG,
                                color: COLOR_SUCCESS_1,
                                borderColor: COLOR_SUCCESS_BACKGROUNG,
                              }}
                              text="Subscribed"
                              fontWeight="bold"
                              handleOnClick={() => {}}
                              isSelected={false}
                              tooltip=""
                            />
                          )}
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item sm={4} md={4} lg={4} sx={{ height: '520px', overflowY: 'scroll' }}>
                      <Box sx={{ border: '2px solid grey', padding: '5px', borderRadius: '10px' }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          Sign up time
                        </Typography>
                        <Typography variant="body1">
                          PHT {convertTimestamp(userDetails.signupTimeStamp, 'Asia/Manila')}
                        </Typography>
                        <Typography variant="body1">
                          UTC {convertTimestamp(userDetails.signupTimeStamp, 'UTC')}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          border: '2px solid grey',
                          padding: '5px',
                          borderRadius: '10px',
                          marginTop: '10px',
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          User Details
                        </Typography>
                        <Typography variant="body1">
                          Occupation: {userDetails?.user_description}
                        </Typography>
                        <Typography variant="body1">
                          No of Listings: {userDetails?.user_sub_description}
                        </Typography>
                        <Typography variant="body1">
                          Landing Page: {userDetails?.landing_page}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          border: '2px solid grey',
                          padding: '5px',
                          borderRadius: '10px',
                          marginTop: '10px',
                        }}
                      >
                        <Typography sx={{ fontWeight: 500 }}>Credits</Typography>
                        <Typography variant="body1">
                          Insights Credits: {userDetails.credits}
                        </Typography>
                        <Typography variant="body1">
                          Estimator Credits: {userDetails.estimatorCredits}
                        </Typography>
                        <Typography variant="body1">
                          Subscription Plan:{' '}
                          {userDetails?.subscription_plan
                            ? userDetails?.subscription_plan
                            : 'Not Subscribed'}
                        </Typography>
                        <Typography variant="body1">
                          Subscription Date:{' '}
                          {userDetails?.subscription_date ? userDetails?.subscription_date : 'N/A'}
                        </Typography>
                        <Button
                          style={{
                            backgroundColor: '#52C879',
                            color: 'white',
                            width: '100%',
                          }}
                          onClick={() => {
                            setOpenAddCreditsModal(!openAddCreditsModal);
                          }}
                        >
                          <Typography style={{ padding: '2px' }}>Give App Access</Typography>
                        </Button>
                      </Box>
                      <Box
                        sx={{
                          border: '2px solid grey',
                          padding: '5px',
                          borderRadius: '10px',
                          marginTop: '10px',
                        }}
                      >
                        <Typography sx={{ fontWeight: 500 }}>Subscribed Countries</Typography>
                        <Box sx={{ display: 'flex' }}>
                          <Typography sx={{ width: '150px' }}>Country Code</Typography>
                          <Typography sx={{ width: '150px' }}>Starting Date</Typography>
                          <Typography sx={{ width: '150px' }}>Ending Date</Typography>
                        </Box>
                        {userDetails.country_wide_access_list.map((country, index) => (
                          <Box key={index} sx={{ display: 'flex' }}>
                            <Typography sx={{ width: '150px' }}>{country.country_code}</Typography>
                            <Typography sx={{ width: '150px' }}>
                              {country.started_at_date.slice(-1)[0]}
                            </Typography>
                            <Typography
                              sx={{
                                width: '150px',
                                color:
                                  new Date(country.ended_at_date.slice(-1)[0]) < new Date()
                                    ? 'red'
                                    : '',
                              }}
                            >
                              {country.ended_at_date.slice(-1)[0]}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      <Button
                        sx={{
                          backgroundColor: 'red',
                          color: 'black',
                          width: '100%',
                          marginTop: '10px',
                        }}
                        onClick={() => {
                          setOpenDeleteUserModal(true);
                        }}
                      >
                        DELETE
                      </Button>
                    </Grid>
                    <Grid item sm={8} md={8} lg={8} sx={{ height: '500px' }}>
                      <Box
                        sx={{
                          border: '2px solid grey',
                          marginLeft: '10px',
                          borderRadius: '10px',
                        }}
                      >
                        <Box
                          sx={{
                            padding: '5px',
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Subscribed Regions ({selectedDashboard.toUpperCase()})
                          </Typography>
                          <Box sx={{ display: 'flex' }}>
                            <Button
                              sx={{
                                backgroundColor: COLOR_GREY_200,
                                border: '1px solid grey',
                                color: 'black',
                              }}
                              onClick={() => {
                                setSelectedDashboard('insights');
                              }}
                            >
                              Insights
                            </Button>
                            <Button
                              sx={{
                                backgroundColor: COLOR_GREY_200,
                                border: '1px solid grey',
                                color: 'black',
                              }}
                              onClick={() => {
                                setSelectedDashboard('estimator');
                              }}
                            >
                              Estimator
                            </Button>
                          </Box>
                        </Box>
                        <Box sx={{ height: 250, width: '100%' }}>
                          {selectedDashboard == 'insights' ? (
                            <DataGrid
                              rows={subscribedInsightRegionRows}
                              columns={subscribedInsightsRegionColumn}
                              hideFooter={true}
                            />
                          ) : (
                            <DataGrid
                              rows={estimatorReportRows}
                              columns={estimatorReportColumn}
                              hideFooter={true}
                            />
                          )}
                        </Box>
                      </Box>
                      {/* For searched regions */}
                      <Box
                        sx={{
                          border: '2px solid grey',
                          marginLeft: '10px',
                          borderRadius: '10px',
                          marginTop: '5px',
                        }}
                      >
                        <Box
                          sx={{
                            padding: '5px',
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Searched Regions (INSIGHTS)
                          </Typography>
                        </Box>
                        <Box sx={{ height: 190, width: '100%' }}>
                          <DataGrid
                            rows={searchedInsightsRegionRows}
                            columns={searchedRegionColumn}
                            hideFooter={true}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default User;
